import { useEffect, useState } from "react";

import { api } from "../lib/api";
import { useT } from "../context/locale";
import { SelectField } from "./ui/form";
import type { Bank } from "../types/api";

/**
 * The bank a player holds an account with.
 *
 * A list rather than a text box, and the *same* list the signup form offers —
 * both read `GET /banks`. It used to be free text here, which meant an operator
 * correcting a registration could type "Maybank Berhad" against a form that only
 * ever produces "Maybank", and nothing downstream could tell the two apart.
 *
 * What is stored is the bank's name, not its code. That is what registrations
 * have always recorded and what an operator reads off a bank transfer.
 */

/**
 * One fetch for the whole console, shared by every field that asks.
 *
 * The list changes with a deploy and not otherwise, so a request per dialog
 * would be the same answer fetched again each time a player is opened. Held as
 * the promise rather than the result, so two dialogs mounting together wait on
 * one request instead of racing to start two.
 */
let pending: Promise<Bank[]> | null = null;

function loadBanks(): Promise<Bank[]> {
  pending ??= api
    .banks()
    .then((response) => response.items)
    .catch((error) => {
      // Not cached: a list that failed on a flaky connection should be
      // retryable by opening the dialog again, not broken until a reload.
      pending = null;
      throw error;
    });
  return pending;
}

export function BankField({
  value,
  onChange,
  label,
  required,
  disabled,
}: {
  value: string;
  onChange: (value: string) => void;
  label: string;
  required?: boolean;
  disabled?: boolean;
}) {
  const t = useT();
  const [banks, setBanks] = useState<Bank[]>([]);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let live = true;
    loadBanks()
      .then((items) => live && setBanks(items))
      .catch(() => live && setFailed(true));
    return () => {
      live = false;
    };
  }, []);

  /*
   * A player whose stored bank is not in the list still shows it.
   *
   * Registrations predate this control, and an entry that was removed from the
   * list — a bank that merged, say — must not silently become a different one
   * the moment somebody opens the player to correct their phone number.
   */
  const unknown = value && !banks.some((bank) => bank.name === value);

  return (
    <SelectField
      label={label}
      value={value}
      onChange={onChange}
      required={required}
      disabled={disabled || (failed && !value)}
      hint={failed ? t("field.bankLoadFailed") : undefined}
      options={[
        { value: "", label: t("common.none") },
        ...(unknown ? [{ value, label: t("field.bankUnlisted", { name: value }) }] : []),
        ...banks.map((bank) => ({ value: bank.name, label: bank.name })),
      ]}
    />
  );
}
